import React, { Component } from "react";
import {
  Card,
  Button,
  FormInput,
  FormSelect,
  FormTextarea,
  FormRadio,
  Loader,
} from "ui-kit-ck-consultant";
import { Row, Col } from "react-flexbox-grid";

import AuthContext from "../../../context/AuthContext";
import axiosConfig from "../../../utils/axiosConfig";
import { getPercentageValue } from "../../../utils/general";

// Audit types offered in-app. `slug` is the audit-type name stored in
// pc2020_audit_type.at_name and also the suffix of the question-definition file
// (data/audit/audit-<slug>.json). It matches the old PHP form file names.
const AUDIT_TYPES = [
  {
    slug: "technique-expert-terrain",
    label: "Audit interne technique terrain réparable",
  },
  { slug: "technique-ead", label: "Audit interne technique EAD" },
  { slug: "technique-riv-vei", label: "Audit interne technique RIV/VEI" },
  { slug: "technique-ve", label: "Audit interne technique VE" },
  { slug: "technique-incoherence", label: "Audit interne technique incohérence" },
  { slug: "technique-vvr", label: "Audit interne technique VVR" },
  { slug: "technique-incendie", label: "Audit interne technique incendie" },
];

export default class Declare extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoad: false,
      step: 0, // 0: type selection, 1: folder reference, 2: audit questions
      type: "", // audit-type slug, e.g. "technique-ead"
      auditFile: null, // parsed question definition for the selected type
      // Step 1 - Folder
      reference: "",
      folderData: null,
      // User options
      userOptions: [],
      userId: "",
      // Step 2 - Answers keyed by "radio-<idx>" -> "1" (Oui) | "0" (Non) | "2" (N.C)
      answers: {},
      commentaire: "",
    };
  }

  static contextType = AuthContext;

  // Load the question definition for an audit type and move to the folder step.
  handleSelectType = (type) => {
    let auditFile = null;
    try {
      auditFile = require(`../../../../../data/audit/audit-${type}.json`);
    } catch (e) {
      auditFile = null;
    }

    if (!auditFile) {
      alert("Impossible de charger le questionnaire de cet audit");
      return;
    }

    this.setState({ type, auditFile, step: 1 }, () => {
      this.loadUsers();
    });
  };

  loadUsers = () => {
    // Audits target experts (u_type 1, 2, 3), like the old form.
    axiosConfig("audit/technical/get-users", {
      data: { userType: [1, 2, 3] },
    }).then((res) => {
      if (res.data.success === true) {
        this.setState({ userOptions: res.data.data || [] });
      }
    });
  };

  handleSearchFolder = () => {
    if (!this.state.reference) return;
    this.setState({ isLoad: true }, () => {
      axiosConfig("audit/technical/get-folder", {
        data: { reference: this.state.reference },
      }).then((res) => {
        if (res.data.success === true) {
          const folder = res.data.data;
          this.setState({
            isLoad: false,
            folderData: folder,
            userId: folder.expert ? folder.expert.toString() : "",
            step: 2,
          });
        } else {
          this.setState({ isLoad: false, folderData: null, step: 2 });
          alert("Dossier non trouvé, vous pouvez continuer sans référence");
        }
      });
    });
  };

  // Flat list of every question across all groups (1-based order = radio index).
  getQuestions = () => {
    if (!this.state.auditFile) return [];
    const questions = [];
    this.state.auditFile.group.forEach((group) => {
      group.data.forEach((question) => {
        questions.push({ label: question[0], points: question[1] });
      });
    });
    return questions;
  };

  // Score = 100 * Σ(points of "Oui") / Σ(points of "Oui"+"Non").
  // Matches the legacy PHP scoring: Oui/Non carry the question's points, N.C
  // carries 0, and the denominator sums only answered (checked) points — so
  // N.C (and blank, which defaults to N.C) questions are excluded from both.
  computeScore = () => {
    const questions = this.getQuestions();
    let totalPoint = 0;
    let currentPoint = 0;
    questions.forEach((question, i) => {
      const answer = this.state.answers[`radio-${i + 1}`] || "2";
      if (answer === "1") {
        totalPoint += question.points;
        currentPoint += question.points;
      } else if (answer === "0") {
        totalPoint += question.points;
      }
    });
    if (totalPoint === 0) return 0;
    return (currentPoint * 100) / totalPoint;
  };

  handleAnswer = (idx, value) => {
    this.setState((prevState) => ({
      answers: { ...prevState.answers, [`radio-${idx}`]: value },
    }));
  };

  handleSubmit = () => {
    if (!this.state.userId) {
      alert("Veuillez sélectionner l'expert audité");
      return;
    }

    const questions = this.getQuestions();
    const value = this.computeScore();

    // Build the a_data blob with the legacy PHP keys so the existing readers
    // (list / CSV export / Modal) keep working unchanged. Unanswered = N.C ("2").
    const formData = {
      dossier: this.state.reference,
      cabinet: this.state.folderData ? this.state.folderData.agency : "",
      user: this.state.userId,
      commentaire: this.state.commentaire,
      value,
      type: this.state.type,
    };
    questions.forEach((question, i) => {
      formData[`radio-${i + 1}`] = this.state.answers[`radio-${i + 1}`] || "2";
    });

    const data = {
      type: this.state.type,
      userId: parseInt(this.state.userId),
      folderId: this.state.folderData ? this.state.folderData.id : null,
      value: Math.round(value),
      commentaire: this.state.commentaire,
      dataJSON: JSON.stringify(formData),
    };

    this.setState({ isLoad: true }, () => {
      axiosConfig("audit/technical/insert-audit", {
        data: { data },
      }).then((res) => {
        if (res.data.success === true) {
          alert("Audit enregistré avec succès");
          this.resetForm();
          if (this.props.onInsert) {
            this.props.onInsert();
          }
        } else {
          this.setState({ isLoad: false });
          alert("Une erreur est survenue");
        }
      });
    });
  };

  resetForm = () => {
    this.setState({
      isLoad: false,
      step: 0,
      type: "",
      auditFile: null,
      reference: "",
      folderData: null,
      userOptions: [],
      userId: "",
      answers: {},
      commentaire: "",
    });
  };

  renderStep0() {
    return (
      <Row>
        {AUDIT_TYPES.map((auditType) => (
          <Col key={auditType.slug} xs={12} md={3} className="mb-10">
            <Button
              outline
              text={auditType.label}
              onClick={() => this.handleSelectType(auditType.slug)}
            />
          </Col>
        ))}
      </Row>
    );
  }

  renderStep1() {
    return (
      <>
        <Row>
          <Col xs={12} md={8}>
            <FormInput
              title="Référence dossier"
              name="reference"
              value={this.state.reference}
              onChange={(e) => this.setState({ reference: e.target.value })}
            />
          </Col>
          <Col xs={12} md={4} className="d-flex">
            <Button
              className="mt-auto"
              text="Rechercher"
              onClick={this.handleSearchFolder}
            />
          </Col>
        </Row>
        <Button
          className="mt-10"
          outline
          text="Annuler"
          onClick={this.resetForm}
        />
      </>
    );
  }

  renderStep2() {
    const score = this.computeScore();
    let idx = 0;
    return (
      <>
        {this.state.folderData ? (
          <div className="mb-10 p-10 bg-gray" style={{ borderRadius: 8 }}>
            <p>
              <b>Dossier :</b> {this.state.reference}
              {this.state.folderData.expertName &&
                ` - Expert : ${this.state.folderData.expertName}`}
            </p>
          </div>
        ) : null}
        <Row>
          <Col xs={12} md={6}>
            <FormSelect
              className="my-10"
              title="Expert audité *"
              name="userId"
              value={this.state.userId}
              options={this.state.userOptions}
              onChange={(e) => this.setState({ userId: e.target.value })}
              ignore={true}
              init="Sélectionner un expert"
            />
          </Col>
        </Row>
        {this.state.auditFile.group.map((group, gIdx) => (
          <div key={gIdx} className="mt-20">
            <h3>{group.title}</h3>
            {group.data.map((question) => {
              idx += 1;
              // Capture idx per-iteration: `idx` is a mutable let shared across
              // the whole render, so an onChange closure over it would read the
              // final (total) value at click time and every radio would write
              // the same key. A const freezes this question's index.
              const questionIdx = idx;
              const name = `radio-${questionIdx}`;
              const value = this.state.answers[name] || "";
              return (
                <Row key={name} middle="xs" className="my-5">
                  <Col xs={12} md={6}>
                    <span>
                      {question[0]}{" "}
                      <b>({question[1]} point(s))</b>
                    </span>
                  </Col>
                  <Col xs={12} md={6}>
                    <Row>
                      <Col xs={4}>
                        <FormRadio
                          text="Oui"
                          name={name}
                          value="1"
                          checked={value === "1"}
                          onChange={() => this.handleAnswer(questionIdx, "1")}
                        />
                      </Col>
                      <Col xs={4}>
                        <FormRadio
                          text="Non"
                          name={name}
                          value="0"
                          checked={value === "0"}
                          onChange={() => this.handleAnswer(questionIdx, "0")}
                        />
                      </Col>
                      <Col xs={4}>
                        <FormRadio
                          text="N.C"
                          name={name}
                          value="2"
                          checked={value === "2"}
                          onChange={() => this.handleAnswer(questionIdx, "2")}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              );
            })}
          </div>
        ))}
        <FormTextarea
          className="my-10"
          title="Commentaires"
          name="commentaire"
          value={this.state.commentaire}
          onChange={(e) => this.setState({ commentaire: e.target.value })}
        />
        <div className="mb-10">
          <b>% d'atteinte : {getPercentageValue(score)}</b>
        </div>
        <Row>
          <Col xs={12} md={6}>
            <Button
              className="mt-10"
              text="Valider"
              onClick={this.handleSubmit}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              className="mt-10"
              outline
              text="Annuler"
              onClick={this.resetForm}
            />
          </Col>
        </Row>
      </>
    );
  }

  render() {
    const selectedType = AUDIT_TYPES.find((t) => t.slug === this.state.type);
    return (
      <Card
        className={this.props.className}
        title={
          this.state.step === 0
            ? "Déclarer un audit interne technique"
            : selectedType
            ? selectedType.label
            : "Audit interne technique"
        }
      >
        <Loader show={this.state.isLoad} />
        {this.state.step === 0 && this.renderStep0()}
        {this.state.step === 1 && this.renderStep1()}
        {this.state.step === 2 && this.renderStep2()}
      </Card>
    );
  }
}
